/**
 * The balance strip — the one place this app spends its personality (DESIGN.md §12.1).
 *
 * A proportional bar of every member's balance, green above the line and rust below,
 * that reads as one glanceable picture of who owes whom. Tapping a segment filters the
 * ledger to that person.
 */

import { useMemo } from "react";

import { formatSigned, toneClass } from "../../lib/money";
import { cn } from "../../lib/utils";
import type { MemberBalance } from "../../lib/api";

interface Props {
  balances: MemberBalance[];
  currency: string;
  myMemberId?: number | null;
  compact?: boolean;
  selectedMemberId?: number | null;
  onSelect?: (memberId: number | null) => void;
  className?: string;
}

interface Segment {
  member: MemberBalance;
  percent: number;
}

function segments(balances: MemberBalance[], sign: 1 | -1): Segment[] {
  const side = balances.filter((balance) =>
    sign > 0 ? balance.balance_base_cents > 0 : balance.balance_base_cents < 0,
  );
  const total = side.reduce((sum, balance) => sum + Math.abs(balance.balance_base_cents), 0);
  if (!total) return [];
  return side
    .slice()
    .sort((a, b) => Math.abs(b.balance_base_cents) - Math.abs(a.balance_base_cents))
    .map((member) => ({
      member,
      percent: (Math.abs(member.balance_base_cents) / total) * 100,
    }));
}

export function BalanceStrip({
  balances,
  currency,
  myMemberId,
  compact = false,
  selectedMemberId,
  onSelect,
  className,
}: Props) {
  const owed = useMemo(() => segments(balances, 1), [balances]);
  const owing = useMemo(() => segments(balances, -1), [balances]);
  const mine = balances.find((balance) => balance.member_id === myMemberId);
  const settled = owed.length === 0 && owing.length === 0;

  const bar = (side: Segment[], tone: "owed" | "owing") => (
    <div
      className={cn("flex w-full gap-px overflow-hidden", compact ? "h-2.5" : "h-9")}
      role="list"
      aria-label={tone === "owed" ? "Members who are owed" : "Members who owe"}
    >
      {side.map(({ member, percent }) => {
        const active = selectedMemberId === member.member_id;
        const label = `${member.display_name} ${formatSigned(member.balance_base_cents, currency)}`;
        // Without an onSelect the strip is a picture, not a control — so it renders as
        // one, rather than as a row of disabled buttons for a screen reader to read out.
        const Element = onSelect ? "button" : "div";
        return (
          <Element
            key={member.member_id}
            {...(onSelect
              ? {
                  type: "button" as const,
                  onClick: () => onSelect(active ? null : member.member_id),
                  "aria-label": `${label}. Filter the ledger to this person.`,
                  "aria-pressed": active,
                }
              : { "aria-hidden": true })}
            role="listitem"
            style={{ width: `${percent}%` }}
            title={label}
            className={cn(
              "group relative min-w-[3px] transition-[filter,opacity]",
              tone === "owed" ? "bg-owed" : "bg-owing",
              active ? "brightness-125" : "hover:brightness-110",
              selectedMemberId && !active ? "opacity-40" : "",
              onSelect ? "cursor-pointer" : "cursor-default",
            )}
          >
            {!compact && percent > 12 ? (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center px-1 text-2xs font-medium text-white/95">
                <span className="truncate">{member.display_name}</span>
              </span>
            ) : null}
          </Element>
        );
      })}
    </div>
  );

  return (
    <div className={cn("w-full", className)}>
      {!compact && (
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <div>
            <p className="text-2xs uppercase tracking-widest text-muted">
              {mine && mine.balance_base_cents > 0
                ? "You are owed"
                : mine && mine.balance_base_cents < 0
                  ? "You owe"
                  : "You are settled up"}
            </p>
            <p className={cn("num text-2xl", mine ? toneClass(mine.balance_base_cents) : "")}>
              {mine ? formatSigned(mine.balance_base_cents, currency) : "—"}
            </p>
          </div>
          {selectedMemberId ? (
            <button
              type="button"
              className="text-xs text-accent underline underline-offset-2"
              onClick={() => onSelect?.(null)}
            >
              Clear filter
            </button>
          ) : null}
        </div>
      )}

      {settled ? (
        <div className="flex h-2 items-center">
          <div className="h-px w-full bg-rule" />
        </div>
      ) : (
        <div className="flex flex-col">
          {bar(owed, "owed")}
          <div className="h-px w-full bg-ink/25" />
          {bar(owing, "owing")}
        </div>
      )}

      {!compact && (
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {balances
            .slice()
            .sort((a, b) => b.balance_base_cents - a.balance_base_cents)
            .map((balance) => (
              <li key={balance.member_id} className="flex items-center gap-1.5">
                <span className="text-muted">{balance.display_name}</span>
                <span className={cn("num", toneClass(balance.balance_base_cents))}>
                  {formatSigned(balance.balance_base_cents, currency)}
                </span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
